// import SelectInput from "@builder/components/general/SelectInput";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import OnboardInput from "../components/onboardInput/OnboardInput";
import { POSOnboardSetup } from "../useApplyOnboardSetup";
import { ValidationResult } from "../Onboard";
import "./step-collect-info.scss";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { Country, State } from "country-state-city";
import SingleSelect from "@builder/components/general/SelectInputNew/SingleSelect";
import AutocompleteInput, { getAddressComponent, getAddressComponentShort, Place } from "@builder/components/general/AutoCompleteInput";
import { TextType } from "@builder/components/general/OliverText";
// @ts-ignore - react-phone-input-2 types may not be available
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OliverText from "@builder/components/general/OliverText";

const ALL_COUNTRIES = Country.getAllCountries();
interface StepCollectInfoProps {
    posOnboardSetup: POSOnboardSetup;
    setPOSOnboardSetup: Dispatch<SetStateAction<POSOnboardSetup>>;
    setValidateCurrentStep?: (validate: () => ValidationResult) => void;
}

// const categoryOptions: Record<string, string> = {
//     retail: "Retail Business",
//     ranchisor: "Franchisor",
//     hospitality: "Hospitality",
//     restaurant: "Restaurant",
//     services: "Services",
//     other: "Other (Please specify)"
// };

// const referralOptions: Record<string, string> = {
//     google: "Google",
//     social: "Social Media",
//     aiConversation: "AI Conversation",
//     friend: "Friend / Colleague",
//     appStore: "App Store",
//     inPersonEvent: "In-person event",
//     ad: "Advertisement",
//     other: "Other (Please specify)"
// };

const StepCollectInfo = ({ posOnboardSetup, setPOSOnboardSetup, setValidateCurrentStep }: StepCollectInfoProps) => {
    // Move showErrors state inside and toggle only after validation attempt
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const { t } = useTranslation();
    const formRef = useRef<HTMLDivElement>(null);
    const states = useMemo(() => State.getStatesOfCountry(posOnboardSetup.outlet?.country || ""), [posOnboardSetup.outlet?.country]);

    const validate = (): ValidationResult => {
        // Sync browser autofill values from DOM to state before validation
        const form = formRef.current;
        let firstName = posOnboardSetup.firstName;
        let lastName = posOnboardSetup.lastName;
        let organizationName = posOnboardSetup.organizationName;
        let address = posOnboardSetup.outlet?.address;
        let city = posOnboardSetup.outlet?.city;
        let postCode = posOnboardSetup.outlet?.postCode;

        if (form) {
            // Find input elements by their placeholders or labels
            const firstNameInput = Array.from(form.querySelectorAll<HTMLInputElement>('input')).find(
                input => input.placeholder?.toLowerCase().includes('first') ||
                    input.getAttribute('aria-label')?.toLowerCase().includes('first')
            );
            const lastNameInput = Array.from(form.querySelectorAll<HTMLInputElement>('input')).find(
                input => input.placeholder?.toLowerCase().includes('last') ||
                    input.getAttribute('aria-label')?.toLowerCase().includes('last')
            );
            const orgNameInput = Array.from(form.querySelectorAll<HTMLInputElement>('input')).find(
                input => input.placeholder?.toLowerCase().includes('organization') ||
                    input.getAttribute('aria-label')?.toLowerCase().includes('organization')
            );

            // Address input is inside AutocompleteInput component
            const addressInput = form.querySelector<HTMLInputElement>('.autocompleteInput input, input.autocompleteInput');
            const cityInput = Array.from(form.querySelectorAll<HTMLInputElement>('input')).find(
                input => input.placeholder?.toLowerCase().includes('city') ||
                    input.getAttribute('aria-label')?.toLowerCase().includes('city')
            );
            const postCodeInput = Array.from(form.querySelectorAll<HTMLInputElement>('input')).find(
                input => (input.placeholder?.toLowerCase().includes('post') || input.placeholder?.toLowerCase().includes('zip')) ||
                    (input.getAttribute('aria-label')?.toLowerCase().includes('post') || input.getAttribute('aria-label')?.toLowerCase().includes('zip'))
            );

            // Get values from DOM (handles browser autofill) or fallback to state
            firstName = firstNameInput?.value?.trim() || firstName;
            lastName = lastNameInput?.value?.trim() || lastName;
            organizationName = orgNameInput?.value?.trim() || organizationName;
            address = addressInput?.value?.trim() || address;
            city = cityInput?.value?.trim() || city;
            postCode = postCodeInput?.value?.trim() || postCode;

            // Sync values from DOM to state if they differ (handles browser autofill)
            const updates: Partial<POSOnboardSetup> = {};
            let needsUpdate = false;

            if (firstName !== posOnboardSetup.firstName) {
                updates.firstName = firstName;
                needsUpdate = true;
            }
            if (lastName !== posOnboardSetup.lastName) {
                updates.lastName = lastName;
                needsUpdate = true;
            }
            if (organizationName !== posOnboardSetup.organizationName) {
                updates.organizationName = organizationName;
                needsUpdate = true;
            }
            if (address !== posOnboardSetup.outlet?.address ||
                city !== posOnboardSetup.outlet?.city ||
                postCode !== posOnboardSetup.outlet?.postCode) {
                if (!updates.outlet) updates.outlet = { ...posOnboardSetup.outlet };
                if (address !== posOnboardSetup.outlet?.address) updates.outlet.address = address;
                if (city !== posOnboardSetup.outlet?.city) updates.outlet.city = city;
                if (postCode !== posOnboardSetup.outlet?.postCode) updates.outlet.postCode = postCode;
                needsUpdate = true;
            }

            // Apply updates if any (syncs autofill to state)
            if (needsUpdate) {
                setPOSOnboardSetup(prev => ({
                    ...prev,
                    ...updates,
                    outlet: updates.outlet ? { ...prev.outlet, ...updates.outlet } : prev.outlet
                }));
            }
        }

        const missingFields: string[] = [];
        if (!firstName) missingFields.push("First Name");
        if (!lastName) missingFields.push("Last Name");
        if (!organizationName) missingFields.push("Organization Name");
        if (!posOnboardSetup.outlet?.country) missingFields.push("Country");
        if (!address) missingFields.push("Address");
        if (!city) missingFields.push("City");
        if (states.length > 0 && !posOnboardSetup.outlet?.state) missingFields.push("State");
        if (!postCode) missingFields.push("Post Code");

        const isValid = missingFields.length === 0;
        setShowErrors(!isValid);

        if (!isValid) {
            return { valid: false, error: `Please fill in: ${missingFields.join(", ")}` };
        }
        return { valid: true };
    };

    useEffect(() => {
        if (setValidateCurrentStep) {
            setValidateCurrentStep(validate);
        }
    }, [setValidateCurrentStep, posOnboardSetup.firstName, posOnboardSetup.lastName, posOnboardSetup.organizationName, posOnboardSetup.outlet]);

    const handleAutoCompleteChange = (place: Place) => {
        // Handle manually typed addresses (no address_components)
        if (!place.address_components || !place.formatted_address) {
            // If manually typed, just save the address string
            if (place.formatted_address) {
                setPOSOnboardSetup(prev => ({
                    ...prev,
                    outlet: {
                        ...prev.outlet,
                        address: place.formatted_address,
                        // Don't clear city/state/postCode if they're already filled
                        // Only clear if address is being cleared
                        ...(place.formatted_address === "" ? {
                            city: undefined,
                            state: undefined,
                            postCode: undefined
                        } : {})
                    }
                }));
            } else {
                // If address is cleared, clear all address fields
                setPOSOnboardSetup(prev => ({
                    ...prev,
                    outlet: {
                        ...prev.outlet,
                        address: undefined,
                        city: undefined,
                        state: undefined,
                        postCode: undefined
                    }
                }));
            }
            return;
        }

        // Extract address components (existing autocomplete logic)
        const streetNumber = getAddressComponent(place, ["street_number"]);
        const route = getAddressComponent(place, ["route"]);
        const city = getAddressComponent(place, ["locality", "administrative_area_level_2"]);
        const stateCode = getAddressComponentShort(place, ["administrative_area_level_1"]);
        const countryCode = getAddressComponentShort(place, ["country"]);
        const postCode = getAddressComponent(place, ["postal_code"]);

        const address1 = [streetNumber, route].filter(Boolean).join(" ").trim() || place.formatted_address;

        const validCountryCode = countryCode && countryCode.length === 2 ? countryCode : undefined;

        setPOSOnboardSetup(prev => ({
            ...prev,
            outlet: {
                ...prev.outlet,
                address: address1 || undefined,
                city: city || undefined,
                state: stateCode || undefined,
                ...(validCountryCode && { country: validCountryCode }),
                postCode: postCode || undefined
            }
        }));
    };

    return (
        <div className="single-onboard-step">
            <div className="collect-info-form" ref={formRef}>
                <div className="two-columns">
                    <OnboardInput
                        label={t("Onboard.first_name")}
                        placeholder={t("Onboard.first_name_placeholder")}
                        value={posOnboardSetup.firstName}
                        onChange={value => setPOSOnboardSetup(prev => ({ ...prev, firstName: value }))}
                        showErrors={showErrors}
                    />

                    <OnboardInput
                        label={t("Onboard.last_name")}
                        placeholder={t("Onboard.last_name_placeholder")}
                        value={posOnboardSetup.lastName}
                        onChange={value => setPOSOnboardSetup(prev => ({ ...prev, lastName: value }))}
                        showErrors={showErrors}
                    />
                </div>
                <OnboardInput
                    label={t("Onboard.organization_name")}
                    placeholder={t("Onboard.your_organization_name_placeholder")}
                    value={posOnboardSetup.organizationName}
                    onChange={value => setPOSOnboardSetup(prev => ({ ...prev, organizationName: value }))}
                    showErrors={showErrors}
                />

                <AutocompleteInput
                    onChange={handleAutoCompleteChange}
                    placeholder={t("Onboard.address_placeholder")}
                    value={posOnboardSetup.outlet?.address as string}
                    country={posOnboardSetup.outlet?.country && posOnboardSetup.outlet.country.length === 2 ? posOnboardSetup.outlet.country : ""}
                    label={t("Onboard.address")}
                />

                <OnboardInput
                    label={t("Onboard.city")}
                    placeholder={t("Onboard.city_placeholder")}
                    value={posOnboardSetup.outlet?.city}
                    onChange={value => setPOSOnboardSetup(prev => ({ ...prev, outlet: { ...prev.outlet, city: value } }))}
                    showErrors={showErrors}
                />

                <SingleSelect
                    options={states}
                    value={states.find(state => state.isoCode === posOnboardSetup.outlet?.state) || null}
                    getOptionLabel={state => state.name}
                    onChange={state => setPOSOnboardSetup(prev => ({ ...prev, outlet: { ...prev.outlet, state: state?.isoCode } }))}
                    label={t("Onboard.state")}
                    labelType={TextType.BODY_LIGHT_XS}
                    labelColor="base-grey"
                    height="small"
                    placeholder={t("Onboard.state_placeholder")}
                />

                <OnboardInput
                    label={t("Onboard.post_code")}
                    placeholder={t("Onboard.post_code_placeholder")}
                    value={posOnboardSetup.outlet?.postCode}
                    onChange={value => setPOSOnboardSetup(prev => ({ ...prev, outlet: { ...prev.outlet, postCode: value } }))}
                    showErrors={showErrors}
                />

                <SingleSelect
                    options={ALL_COUNTRIES}
                    value={ALL_COUNTRIES.find(country => country.isoCode === posOnboardSetup.outlet?.country) || null}
                    getOptionLabel={country => country.name}
                    onChange={country => {
                        setPOSOnboardSetup(prev => ({
                            ...prev,
                            outlet: {
                                ...prev.outlet,
                                country: country?.isoCode,
                                address: undefined,
                                city: undefined,
                                state: undefined,
                                postCode: undefined
                            }
                        }));
                    }}
                    label={t("Onboard.country")}
                    labelType={TextType.BODY_LIGHT_XS}
                    labelColor="base-grey"
                    height="small"
                    placeholder={t("Onboard.country_placeholder")}
                />

                <div className="onboard-input-wrapper">
                    <OliverText size="xs" color="base-grey" weight="light">{`${t("Onboard.phone")}(${t("Onboard.optional")})`}</OliverText>
                    <PhoneInput
                        country={posOnboardSetup.outlet?.country?.toLowerCase() || "us"}
                        value={posOnboardSetup.outlet?.phone || ""}
                        onChange={(value: string) => setPOSOnboardSetup(prev => ({ ...prev, outlet: { ...prev.outlet, phone: value } }))}
                        placeholder={t("Onboard.phone_placeholder")}
                        inputStyle={{
                            width: "100%",
                            height: "36px",
                            fontSize: "14px",
                            border: "1px solid #e5e0eb",
                            borderRadius: "8px",
                            outline: "none",
                            fontFamily: "inherit"
                        }}
                        buttonStyle={{
                            backgroundColor: "inherit",
                            border: "1px solid #e5e0eb",
                            borderRadius: "8px 0 0 8px"
                        }}
                        containerStyle={{
                            width: "100%"
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default StepCollectInfo;
